import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import NavBar from '../components/layout/NavBar'
import { acceptInviteJob, claimLocalJob, declineInviteJob, declineLocalDelivery } from '../api/jobs'
import { getInbox, getInboxCount, type ProRequestInboxItem } from '../api/prosRequests'

type InboxTab = 'PENDING' | 'CLAIMED' | 'DECLINED' | 'EXPIRED'

function relativeTime(targetIso?: string | null) {
  if (!targetIso) return ''
  const t = new Date(targetIso).getTime()
  if (!Number.isFinite(t)) return ''
  const now = Date.now()
  const diffMs = t - now
  const abs = Math.abs(diffMs)
  const hours = Math.floor(abs / (1000 * 60 * 60))
  const mins = Math.floor((abs % (1000 * 60 * 60)) / (1000 * 60))
  const text = `${hours}h ${mins}m`
  return diffMs >= 0 ? `in ${text}` : `${text} ago`
}

export default function ProsRequests() {
  const [items, setItems] = useState<ProRequestInboxItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [pendingCount, setPendingCount] = useState(0)
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [tab, setTab] = useState<InboxTab>('PENDING')

  function formatPixeFromCents(cents?: number | null): string | null {
    if (cents == null) return null
    const whole = cents / 100
    if (!Number.isFinite(whole)) return null
    if (cents % 100 === 0) return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(whole)
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(whole)
  }

  function compensationSnippet(job: ProRequestInboxItem['job']): string {
    if (job.compensation_mode === 'HOURLY') {
      if (typeof job.proposed_hourly_pixe === 'number' && job.proposed_hourly_pixe > 0) return `Hourly ${job.proposed_hourly_pixe} PIXE/hr`
      const min = formatPixeFromCents(job.min_rate_cents)
      const max = formatPixeFromCents(job.max_rate_cents)
      if (min && max && min !== max) return `Hourly ${min}-${max} PIXE/hr`
      if (min) return `Hourly ${min} PIXE/hr`
    }
    if (job.compensation_mode === 'FIXED') {
      if (typeof job.proposed_fixed_price_pixe === 'number' && job.proposed_fixed_price_pixe > 0) return `Fixed ${job.proposed_fixed_price_pixe} PIXE`
      const fixed = formatPixeFromCents(job.fixed_price_cents)
      if (fixed) return `Fixed ${fixed} PIXE`
    }
    return 'Comp not set'
  }

  async function load(nextTab = tab) {
    setLoading(true)
    setError(null)
    try {
      const [inbox, count] = await Promise.all([getInbox(1, 50, nextTab), getInboxCount()])
      setItems(Array.isArray(inbox?.results) ? inbox.results : [])
      setPendingCount(Number(count?.pending_count || 0))
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load requests inbox')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load(tab)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  async function refreshCountOnly() {
    try {
      const count = await getInboxCount()
      setPendingCount(Number(count?.pending_count || 0))
    } catch {
      // no-op
    }
  }

  async function runRowAction(
    key: string,
    fn: () => Promise<any>,
    successMessage: string,
    opts?: { optimisticRemove?: boolean; alreadyClaimedMessage?: string }
  ) {
    setBusyKey(key)
    setError(null)
    setNotice(null)
    try {
      await fn()
      if (opts?.optimisticRemove) {
        setItems((prev) => prev.filter((row) => `${row.type}:${row.job?.id}` !== key))
      }
      setNotice(successMessage)
      await refreshCountOnly()
      await load(tab)
    } catch (e: any) {
      const code = String(e?.response?.data?.code || '')
      if (opts?.alreadyClaimedMessage && code === 'ALREADY_CLAIMED') {
        setItems((prev) => prev.filter((row) => `${row.type}:${row.job?.id}` !== key))
        setNotice(opts.alreadyClaimedMessage)
        await refreshCountOnly()
        await load(tab)
      } else {
        setError(e?.response?.data?.detail || e?.response?.data?.message || code || e?.message || 'Action failed')
      }
    } finally {
      setBusyKey(null)
    }
  }

  async function onClaim(jobId: number) {
    await runRowAction(
      `delivery:${jobId}`,
      () => claimLocalJob(jobId),
      'Request claimed.',
      { optimisticRemove: true, alreadyClaimedMessage: 'Another pro claimed this request.' }
    )
  }

  async function onDecline(jobId: number) {
    await runRowAction(`delivery:${jobId}`, () => declineLocalDelivery(jobId), 'Request declined.', { optimisticRemove: true })
  }

  async function onAcceptInvite(jobId: number) {
    await runRowAction(`invite:${jobId}`, () => acceptInviteJob(jobId), 'Invite accepted.', { optimisticRemove: true })
  }

  async function onDeclineInvite(jobId: number) {
    await runRowAction(`invite:${jobId}`, () => declineInviteJob(jobId), 'Invite declined.', { optimisticRemove: true })
  }

  return (
    <div className="min-h-screen">
      <NavBar showSearch={false} />
      <main className="mx-auto max-w-4xl px-4 py-6 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-extrabold">Requests</h1>
            <p className="text-sm text-white/65">Invites and local requests sent to you.</p>
          </div>
          <div className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-sm">Pending: {pendingCount}</div>
        </div>

        <div className="flex flex-wrap gap-2">
          {(['PENDING', 'CLAIMED', 'DECLINED', 'EXPIRED'] as InboxTab[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setTab(s)}
              className={`rounded-full border px-3 py-1.5 text-xs ${tab === s ? 'border-brand bg-brand/20 text-white' : 'border-white/15 bg-white/5 text-white/75'}`}
            >
              {s[0] + s.slice(1).toLowerCase()}
            </button>
          ))}
        </div>

        {error ? <div className="rounded-lg border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-200">{error}</div> : null}
        {notice ? <div className="rounded-lg border border-emerald-400/30 bg-emerald-500/10 p-3 text-sm text-emerald-200">{notice}</div> : null}
        {loading ? <div className="glass h-24 rounded-xl" /> : null}
        {!loading && items.length === 0 ? <div className="text-sm text-white/70">No {tab.toLowerCase()} requests.</div> : null}

        <div className="space-y-3">
          {items.map((item) => {
            const job = item.job || {}
            const jobId = Number(job.id || 0)
            const rowKey = `${item.type}:${jobId}`
            const disabled = busyKey === rowKey
            const isDelivery = item.type === 'delivery'
            const isInvite = item.type === 'invite'
            const expiryText = item.status === 'PENDING'
              ? (item.expires_at ? `Expires ${relativeTime(item.expires_at)}` : '')
              : item.status === 'EXPIRED'
                ? `Expired ${relativeTime(item.expired_at || item.updated_at)}`
                : item.status[0] + item.status.slice(1).toLowerCase()
            const modeText = String(job.job_mode || '').toUpperCase() === 'LOCAL' ? 'Local' : 'Remote'
            const escrowHeld = String(job.escrow_status || '').toUpperCase() === 'HELD'
            const inviteStatus = String(job.invitation_status || '').toUpperCase()
            const showInviteActions = isInvite && tab === 'PENDING' && inviteStatus === 'PENDING'
            const showDeliveryActions = isDelivery && item.status === 'PENDING'

            return (
              <div key={item.id} className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm text-white/70">{new Date(item.created_at).toLocaleString()}</div>
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] ${isDelivery ? 'border-cyan-400/40 bg-cyan-500/10 text-cyan-100' : 'border-violet-400/40 bg-violet-500/10 text-violet-100'}`}>
                    {isDelivery ? 'Pros to Go' : 'Invite'}
                  </span>
                </div>
                <div className="text-lg font-semibold">{job.title || 'Request'}</div>
                <div className="text-sm text-white/85">{job.summary || ''}</div>
                <div className="text-xs text-white/70">
                  {job.client?.display_name ? `${job.client.display_name} | ` : ''}
                  {job.client_location_text || ''} {job.client_zipcode ? `(${job.client_zipcode})` : ''} | {job.urgency || 'FLEXIBLE'}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-white/75">
                  <span className="rounded-full border border-white/15 bg-black/30 px-2 py-0.5">{modeText}</span>
                  <span className="rounded-full border border-white/15 bg-black/30 px-2 py-0.5">{compensationSnippet(job)}</span>
                  <span className={`rounded-full border px-2 py-0.5 ${escrowHeld ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-100' : 'border-white/15 bg-black/30 text-white/70'}`}>
                    {escrowHeld ? 'Escrow funded' : 'Escrow not funded'}
                  </span>
                  {job.scheduled_for ? <span className="rounded-full border border-white/15 bg-black/30 px-2 py-0.5">Scheduled {new Date(job.scheduled_for).toLocaleString()}</span> : null}
                </div>
                {expiryText ? <div className="text-xs text-white/60">{expiryText}</div> : null}
                <div className="flex items-center gap-2">
                  <Link to={`/pros/requests/${encodeURIComponent(item.id)}`} className="rounded-lg border border-white/20 bg-white/5 px-3 py-1.5 text-sm hover:bg-white/10">Open</Link>
                  {showDeliveryActions ? (
                    <>
                      <button
                        type="button"
                        onClick={() => onClaim(jobId)}
                        disabled={disabled}
                        className="rounded-lg bg-emerald-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-600 disabled:opacity-60"
                      >
                        {disabled ? 'Working...' : 'Claim'}
                      </button>
                      <button
                        type="button"
                        onClick={() => onDecline(jobId)}
                        disabled={disabled}
                        className="rounded-lg border border-white/20 bg-white/5 px-3 py-1.5 text-sm hover:bg-white/10 disabled:opacity-60"
                      >
                        Decline
                      </button>
                    </>
                  ) : null}
                  {showInviteActions ? (
                    <>
                      <button
                        type="button"
                        onClick={() => onAcceptInvite(jobId)}
                        disabled={disabled}
                        className="rounded-lg bg-emerald-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-600 disabled:opacity-60"
                      >
                        {disabled ? 'Working...' : 'Accept'}
                      </button>
                      <button
                        type="button"
                        onClick={() => onDeclineInvite(jobId)}
                        disabled={disabled}
                        className="rounded-lg border border-white/20 bg-white/5 px-3 py-1.5 text-sm hover:bg-white/10 disabled:opacity-60"
                      >
                        Decline
                      </button>
                    </>
                  ) : null}
                  {isInvite && inviteStatus === 'ACCEPTED' ? (
                    <span className="rounded-full border border-emerald-400/35 bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-100">Accepted</span>
                  ) : null}
                  {isInvite && inviteStatus === 'DECLINED' ? (
                    <span className="rounded-full border border-white/20 bg-white/5 px-2 py-0.5 text-[11px] text-white/75">Declined</span>
                  ) : null}
                  {isInvite && job.thread_id ? (
                    <Link to={`/inbox/i/${job.thread_id}`} className="rounded-lg border border-white/20 bg-white/5 px-3 py-1.5 text-sm hover:bg-white/10">Chat</Link>
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
      </main>
    </div>
  )
}
